import { useEffect, useState } from "react";
import { api } from "@/api";
import { Plus, Trash2, Edit3, X, Save, ChevronDown } from "lucide-react";

const slugify = (s) => (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

/**
 * Generic CRUD page for simple entities. Field types: text, textarea, number, slug, boolean, select, image, list, list-of-pairs
 */
export default function AdminCrud({
  endpoint,
  title,
  singular,
  fields,
  listColumns,
  defaults = {},
}) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    api.get(`/admin/${endpoint}`).then((r) => { setItems(r.data || []); setLoading(false); });
  };

  useEffect(reload, [endpoint]);

  const save = async () => {
    const payload = { ...editing };
    if (editing.id) {
      const id = editing.id;
      delete payload.id;
      await api.put(`/admin/${endpoint}/${id}`, payload);
    } else {
      await api.post(`/admin/${endpoint}`, payload);
    }
    setEditing(null);
    reload();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/admin/${endpoint}/${id}`);
    reload();
  };

  return (
    <div data-testid={`admin-${endpoint}`}>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading font-bold text-3xl text-[#1C1C1C]">{title}</h1>
          <p className="mt-1 font-body text-[#4B5563]">Manage your {title.toLowerCase()}.</p>
        </div>
        <button onClick={() => setEditing({ ...defaults })} className="btn-primary" data-testid="admin-new-btn">
          <Plus size={16} /> New {singular}
        </button>
      </div>

      <div className="bg-white border border-[#E5E7EB]">
        {loading ? (
          <div className="p-10 text-center text-[#4B5563]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-[#4B5563]">Nothing here yet. Click "New {singular}" to add one.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-[#E5E7EB]">
              <tr>
                {listColumns.map((c) => <th key={c.key} className="px-5 py-3 text-left font-heading font-semibold text-xs uppercase tracking-wider text-[#4B5563]">{c.label}</th>)}
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-[#F5F5F5]" data-testid={`row-${it.id}`}>
                  {listColumns.map((c) => (
                    <td key={c.key} className="px-5 py-4 font-body">
                      {c.render ? c.render(it) : (typeof it[c.key] === "boolean" ? (it[c.key] ? "Yes" : "No") : (Array.isArray(it[c.key]) ? it[c.key].length : (it[c.key] || "—")))}
                    </td>
                  ))}
                  <td className="px-5 py-4 text-right">
                    <button onClick={() => setEditing(it)} className="p-2 text-[#4B5563] hover:text-[#C1121F]" data-testid={`edit-${it.id}`}><Edit3 size={16} /></button>
                    <button onClick={() => del(it.id)} className="p-2 text-[#4B5563] hover:text-[#C1121F]" data-testid={`delete-${it.id}`}><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <Drawer onClose={() => setEditing(null)} title={`${editing.id ? "Edit" : "New"} ${singular}`} onSave={save}>
          {fields.map((f) => (
            <FieldEditor
              key={f.key}
              field={f}
              value={editing[f.key]}
              onChange={(v) => setEditing((e) => {
                const next = { ...e, [f.key]: v };
                // Auto-generate slug from name/title if slug is empty
                if (f.autoSlugFrom && !next.slug && (f.key === f.autoSlugFrom)) next.slug = slugify(v);
                return next;
              })}
              all={editing}
              setAll={setEditing}
            />
          ))}
        </Drawer>
      )}
    </div>
  );
}

export function Drawer({ children, onClose, title, onSave }) {
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="admin-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="ml-auto bg-white w-full max-w-2xl h-full overflow-y-auto relative">
        <div className="sticky top-0 bg-white border-b border-[#E5E7EB] px-6 py-4 flex items-center justify-between z-10">
          <h2 className="font-heading font-bold text-xl text-[#1C1C1C]">{title}</h2>
          <div className="flex items-center gap-2">
            <button onClick={onSave} className="btn-primary" data-testid="drawer-save"><Save size={16} /> Save</button>
            <button onClick={onClose} className="p-2 text-[#4B5563] hover:text-[#C1121F]" data-testid="drawer-close"><X size={20} /></button>
          </div>
        </div>
        <div className="p-6 space-y-5">{children}</div>
      </div>
    </div>
  );
}

export function FieldEditor({ field, value, onChange, all, setAll }) {
  const label = field.label || field.key;
  const lab = (
    <label className="block text-xs uppercase tracking-wider font-heading font-semibold text-[#4B5563] mb-2">
      {label}{field.required && <span className="text-[#C1121F]"> *</span>}
    </label>
  );

  if (field.type === "textarea") return (<div>{lab}<textarea rows={field.rows || 6} value={value || ""} onChange={(e) => onChange(e.target.value)} className="input-light" data-testid={`field-${field.key}`} /></div>);
  if (field.type === "boolean") return (<label className="flex items-center gap-3 cursor-pointer" data-testid={`field-${field.key}`}><input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="accent-[#C1121F] w-4 h-4" /><span className="font-heading font-medium text-sm text-[#1C1C1C]">{label}</span></label>);
  if (field.type === "number") return (<div>{lab}<input type="number" value={value ?? 0} onChange={(e) => onChange(parseInt(e.target.value || "0", 10))} className="input-light" data-testid={`field-${field.key}`} /></div>);
  if (field.type === "select") return (<div>{lab}<select value={value || ""} onChange={(e) => onChange(e.target.value)} className="input-light" data-testid={`field-${field.key}`}>{field.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>);
  if (field.type === "list") return (<div>{lab}<ListInput value={value || []} onChange={onChange} placeholder={field.placeholder || "Add item and press Enter"} testid={`field-${field.key}`} /></div>);
  if (field.type === "pairs") return (<div>{lab}<PairsInput value={value || []} onChange={onChange} testid={`field-${field.key}`} /></div>);
  if (field.type === "images") return (<div>{lab}<ImagesInput value={value || []} onChange={onChange} testid={`field-${field.key}`} /></div>);
  if (field.type === "image") return (<div>{lab}<ImagesInput value={value ? [value] : []} onChange={(v) => onChange(v[0] || "")} single testid={`field-${field.key}`} /></div>);
  if (field.type === "category-select") return (
    <div>{lab}
      <CategorySelect value={value} onChange={onChange} testid={`field-${field.key}`} />
    </div>
  );
  return (
    <div>{lab}
      <input
        type="text"
        value={value || ""}
        onChange={(e) => {
          let v = e.target.value;
          onChange(v);
          if (field.autoSlug && setAll) setAll((a) => ({ ...a, slug: a.slug ? a.slug : slugify(v) }));
        }}
        className="input-light"
        placeholder={field.placeholder}
        data-testid={`field-${field.key}`}
      />
    </div>
  );
}

function ListInput({ value, onChange, placeholder, testid }) {
  const [input, setInput] = useState("");
  const add = () => { if (input.trim()) { onChange([...value, input.trim()]); setInput(""); } };
  return (
    <div data-testid={testid}>
      <div className="flex gap-2">
        <input type="text" value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder} className="input-light" />
        <button type="button" onClick={add} className="btn-outline-dark px-4">Add</button>
      </div>
      {value.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-2">
          {value.map((v, i) => (
            <li key={i} className="inline-flex items-center gap-2 bg-[#F5F5F5] px-3 py-1.5 text-sm font-body">
              {v}
              <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-[#C1121F]"><X size={12} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PairsInput({ value, onChange, testid }) {
  const update = (i, k, v) => onChange(value.map((p, j) => j === i ? { ...p, [k]: v } : p));
  return (
    <div data-testid={testid}>
      <div className="space-y-2">
        {value.map((p, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2">
            <input value={p.key || ""} onChange={(e) => update(i, "key", e.target.value)} placeholder="Key (e.g. Material)" className="input-light" />
            <input value={p.value || ""} onChange={(e) => update(i, "value", e.target.value)} placeholder="Value (e.g. Carbon Steel)" className="input-light" />
            <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="px-3 text-[#C1121F]"><X size={14} /></button>
          </div>
        ))}
      </div>
      <button type="button" onClick={() => onChange([...value, { key: "", value: "" }])} className="btn-outline-dark mt-3 px-4 text-sm">+ Add row</button>
    </div>
  );
}

function ImagesInput({ value, onChange, single, testid }) {
  const [url, setUrl] = useState("");
  const fileToBase64 = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });
  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    const datas = await Promise.all(files.map(fileToBase64));
    onChange(single ? [datas[0]] : [...value, ...datas]);
  };
  const addUrl = () => { if (url.trim()) { onChange(single ? [url.trim()] : [...value, url.trim()]); setUrl(""); } };
  return (
    <div data-testid={testid}>
      <div className="flex gap-2 items-center">
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Paste image URL or upload below" className="input-light flex-1" />
        <button type="button" onClick={addUrl} className="btn-outline-dark px-4">Add URL</button>
      </div>
      <div className="mt-2">
        <input type="file" multiple={!single} accept="image/*" onChange={onFile} className="block text-sm text-[#4B5563]" />
      </div>
      {value.length > 0 && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          {value.map((v, i) => (
            <div key={i} className="relative aspect-square border border-[#E5E7EB]">
              <img src={v} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-[#C1121F] text-white p-1"><X size={12} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CategorySelect({ value, onChange, testid }) {
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data || [])); }, []);
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="input-light" data-testid={testid}>
      <option value="">— Select category —</option>
      {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
    </select>
  );
}
